import { reactive, ref } from "vue";
import { defineStore } from "pinia";
import { IApp } from "@/types";
import { makeAlert } from "@/utils";
import _ from "lodash";
import Report from "@/actions/Report";
import { useAuthStore } from "./auth";
import Auth from "@/utils/auth";

export const useReportStore = defineStore("report", () => {
  const state = reactive<{
    reportsListStatus: IApp.ILoadingStatus;
    reports: IApp.IReport[];
    prompt: string;
  }>({
    reportsListStatus: IApp.ILoadingStatus.Idle,
    reports: [],
    prompt: ""
  });

  const promptLoadingStatus = ref<IApp.ILoadingStatus>(IApp.ILoadingStatus.Idle);

  const reports = ref<IApp.IReport[]>([]);

  const fetchReports = async () => {
    state.reportsListStatus = IApp.ILoadingStatus.Loading;
    const response = await Report.list();
    if (response.status) {
      reports.value = _(response.data.reports).sortBy(["requestId", "createdAt"]).reverse().value();
      state.reportsListStatus = IApp.ILoadingStatus.Success;
      state.reports = reports.value;
    } else {
      makeAlert(response.message, "error");
      state.reportsListStatus = IApp.ILoadingStatus.Error;
    }
  };

  const getReport = async (requestId: string, reportId: string) => {
    const response = await Report.get(requestId, reportId);
    if (response.status) {
      if (state.reports.find((r) => r.requestId === requestId && r.id === reportId)) {
        const index = state.reports.findIndex(
          (r) => r.requestId === requestId && r.id === reportId
        );
        state.reports[index] = response.data.report;
      } else {
        state.reports.push(response.data.report);
      }
      return response.data.report;
    } else {
      makeAlert(response.message, "error");
      return null;
    }
  };

  const findReport = (requestId: string, reportId: string): IApp.IReport | undefined => {
    return state.reports.find((r) => r.requestId === requestId && r.id === reportId);
  };

  const fetchPrompt = async () => {
    const authStore = useAuthStore();
    if (!authStore.hasPermission(Auth.Permission.REPORT_PROMPT_GET)) {
      return "";
    }
    promptLoadingStatus.value = IApp.ILoadingStatus.Loading;
    const response = await Report.getPrompt();
    if (response.status) {
      state.prompt = response.data.prompt;
      promptLoadingStatus.value = IApp.ILoadingStatus.Success;
      return state.prompt;
    } else {
      makeAlert(response.message, "error");
      promptLoadingStatus.value = IApp.ILoadingStatus.Error;
    }
    return "";
  };

  const getPrompt = () => {
    return state.prompt;
  };

  const updatePrompt = (prompt: string) => {
    state.prompt = prompt;
  };

  const savePrompt = async () => {
    const response = await Report.updatePrompt(state.prompt);
    if (response.status) {
      makeAlert("Prompt updated successfully.", "success");
    } else {
      makeAlert(response.message, "error");
    }
  };

  return {
    reports,
    state,
    promptLoadingStatus,
    fetchReports,
    getReport,
    findReport,
    fetchPrompt,
    getPrompt,
    updatePrompt,
    savePrompt
  };
});
